// 창고 재고 관리 — 입고(+) · 일별 배송 차감(−) · 월말 택배 차감(−) 으로 현 보유량(박스) 계산. 타사 대행 배송분은 맡긴 회사 재고라 빼지 않는다(F5)
import { useEffect, useState } from "react";
import { collection, addDoc, deleteDoc, doc, onSnapshot, serverTimestamp, orderBy, where } from "firebase/firestore";
import { db } from "@/lib/firebase";
import { orgQuery, withOrg } from "@/lib/org";
import { useStudents } from "@/hooks/useStudents";
import { useAuth } from "@/store/auth";
import { fmtYearMonth } from "@/lib/format";
import { plainError } from "@/lib/plainError";

interface Movement {
  id: string;
  type: "in" | "courier" | "adjust";
  boxes: number;
  note?: string;
  by?: string;
  yearMonth?: string;
  createdAt?: { seconds: number } | null;
}

const UNITS_PER_BOX = 24;

export default function InventoryPage() {
  const { user } = useAuth();
  const { students: courierStudents } = useStudents({ courier: true }); // 월말 택배 대상
  const [movements, setMovements] = useState<Movement[]>([]);
  const [deliveryBoxes, setDeliveryBoxes] = useState(0);
  const [proxyBoxes, setProxyBoxes] = useState(0); // 대행 배송분(맡긴 회사 재고 — 우리 재고에서 빼지 않음)
  const [proxyIds, setProxyIds] = useState<Set<string> | null>(null); // 대행 학생 — 불러오기 전엔 null
  const [inBoxes, setInBoxes] = useState("");
  const [inNote, setInNote] = useState("");
  const [busy, setBusy] = useState(false);
  const ym = fmtYearMonth();

  // 재고 이동 내역
  useEffect(() => {
    const unsub = onSnapshot(orgQuery("stockMovements", orderBy("createdAt", "desc")), (snap) => {
      setMovements(snap.docs.map((d) => ({ id: d.id, ...(d.data() as any) })));
    });
    return () => unsub();
  }, []);

  // 타사 대행 학생 — 배송 기록에는 대행 표시가 없어 학생 문서의 isProxy로 가른다.
  // 단순화: 지금 명단 기준이라, 대행 학생을 지우거나 자사로 바꾸면 그 학생의 지난 배송이 다시 우리 재고 차감으로 잡힌다. 배송 기록에 대행 표시를 남기게 되면 그걸로 바꾼다
  useEffect(() => {
    const unsub = onSnapshot(
      orgQuery("students", where("isProxy", "==", true)),
      (snap) => setProxyIds(new Set(snap.docs.map((d) => d.id))),
      () => setProxyIds(new Set()),
    );
    return () => unsub();
  }, []);

  // 배송 차감 — deliveries 의 boxCount 누적 합 (실시간). 대행 학생 배송분은 따로 센다
  useEffect(() => {
    if (!proxyIds) return;
    const unsub = onSnapshot(orgQuery("deliveries"), (snap) => {
      let sum = 0;
      let proxy = 0;
      snap.docs.forEach((d) => {
        const x = d.data() as any;
        const boxes = x.boxCount || 1;
        if (proxyIds.has(String(x.studentId))) proxy += boxes;
        else sum += boxes;
      });
      setDeliveryBoxes(sum);
      setProxyBoxes(proxy);
    });
    return () => unsub();
  }, [proxyIds]);

  const stockIn = movements.filter((m) => m.type === "in").reduce((s, m) => s + (m.boxes || 0), 0);
  const adjust = movements.filter((m) => m.type === "adjust").reduce((s, m) => s + (m.boxes || 0), 0);
  const courierOut = movements.filter((m) => m.type === "courier").reduce((s, m) => s + (m.boxes || 0), 0);
  const current = stockIn + adjust - deliveryBoxes - courierOut;

  const courierCount = courierStudents.filter((s) => !s.isProxy).length; // 이번달 택배 대상 = 박스 수(대행 학생은 맡긴 회사 재고라 뺀다)
  const proxyCourier = courierStudents.length - courierCount;
  const shippedThisMonth = movements.find((m) => m.type === "courier" && m.yearMonth === ym);

  const addStock = async () => {
    const n = Number(inBoxes);
    if (!n || busy) return;
    setBusy(true);
    try {
      await addDoc(collection(db, "stockMovements"), withOrg({
        type: "in", boxes: n, note: inNote, by: user?.email || user?.uid || "", createdAt: serverTimestamp(),
      }));
      setInBoxes(""); setInNote("");
    } finally { setBusy(false); }
  };

  const shipCourier = async () => {
    if (!courierCount || shippedThisMonth || busy) return;
    setBusy(true);
    try {
      await addDoc(collection(db, "stockMovements"), withOrg({
        type: "courier", boxes: courierCount, note: `${ym} 월말 택배 ${courierCount}명`,
        yearMonth: ym, by: user?.email || user?.uid || "", createdAt: serverTimestamp(),
      }));
    } finally { setBusy(false); }
  };

  const removeMovement = async (id: string) => {
    if (!confirm("이 이동 기록을 삭제할까요?")) return;
    try {
      await deleteDoc(doc(db, "stockMovements", id));
    } catch (e) {
      alert(`기록을 지우지 못했습니다 — ${plainError(e)}`); // 실패해도 아무 말 없던 문제(4차 점검 71)
    }
  };

  const fmtDate = (m: Movement) =>
    m.createdAt?.seconds ? new Date(m.createdAt.seconds * 1000).toLocaleDateString("ko-KR") : "—";
  const typeLabel = (t: string) => (t === "in" ? "입고" : t === "courier" ? "월말택배" : "조정");

  return (
    <div className="space-y-4">
      <h1 className="text-xl font-bold">📦 재고 관리</h1>

      {/* 현 보유량 */}
      <section className="card p-5 bg-milk-50 border-milk-200 text-center">
        <div className="label">현 창고 보유량</div>
        <div className={`text-4xl font-extrabold mt-1 ${current < 0 ? "text-rose-600" : "text-milk-700"}`}>
          {current.toLocaleString()} 박스
        </div>
        <div className="text-xs text-slate-400 mt-1">박스 단위 관리 · 1박스 {UNITS_PER_BOX}개입</div>
        {current < 0 && <div className="text-xs text-rose-600 mt-1">⚠️ 재고가 음수입니다 — 입고 누락을 확인하세요.</div>}
      </section>

      {/* 요약 */}
      <section className="grid grid-cols-3 gap-3">
        <Stat title="총 입고" value={`+${stockIn.toLocaleString()}`} tone="emerald" />
        <Stat title="배송 차감" value={`−${deliveryBoxes.toLocaleString()}`} />
        <Stat title="택배 차감" value={`−${courierOut.toLocaleString()}`} />
      </section>
      {(proxyBoxes > 0 || proxyCourier > 0) && (
        <p className="text-sm text-slate-600">
          🚛 타사 대행 배송 {proxyBoxes.toLocaleString()}박스{proxyCourier ? ` · 대행 택배 대상 ${proxyCourier}명` : ""}는 맡긴 회사의 재고라 위 보유량에서 빼지 않았습니다.
        </p>
      )}

      {/* 입고 등록 */}
      <section className="card p-4 space-y-3">
        <h3 className="font-bold">➕ 입고 등록</h3>
        <div className="flex flex-wrap items-end gap-2">
          <label className="block">
            <div className="label">입고 박스 수</div>
            <input
              type="number"
              className="input w-32 mt-1"
              placeholder="0"
              value={inBoxes}
              onChange={(e) => setInBoxes(e.target.value)}
            />
          </label>
          <label className="block flex-1 min-w-[150px]">
            <div className="label">메모 (선택)</div>
            <input
              className="input mt-1"
              placeholder="예: 6월 1차 입고 / 서울우유"
              value={inNote}
              onChange={(e) => setInNote(e.target.value)}
            />
          </label>
          <button className="btn-primary" onClick={addStock} disabled={busy || !Number(inBoxes)}>입고</button>
        </div>
      </section>

      {/* 월말 택배 차감 */}
      <section className="card p-4">
        <h3 className="font-bold mb-1">🚚 월말 택배 발송 처리</h3>
        <p className="text-xs text-slate-500 mb-3">
          이번달({ym}) 월말 택배 대상 <strong>{courierCount}명 = {courierCount}박스</strong>를 재고에서 차감합니다.
        </p>
        {shippedThisMonth ? (
          <div className="text-sm text-emerald-700">
            ✓ {ym} 택배 발송 처리됨 ({shippedThisMonth.boxes}박스 차감)
          </div>
        ) : (
          <button className="btn-secondary" onClick={shipCourier} disabled={busy || !courierCount}>
            {ym} 택배 발송 처리 ({courierCount}박스 차감)
          </button>
        )}
      </section>

      {/* 이동 내역 */}
      <section className="card overflow-auto max-h-[50vh]">
        <table className="min-w-full text-sm">
          <thead className="sticky top-0 bg-slate-50 text-slate-500 text-left">
            <tr>
              <th className="p-2">날짜</th>
              <th className="p-2">구분</th>
              <th className="p-2 text-right">박스</th>
              <th className="p-2">메모</th>
              <th className="p-2"></th>
            </tr>
          </thead>
          <tbody>
            {movements.map((m) => (
              <tr key={m.id} className="border-t">
                <td className="p-2 text-slate-500">{fmtDate(m)}</td>
                <td className="p-2">
                  <span className={`chip text-[10px] ${m.type === "in" ? "bg-emerald-100 text-emerald-700" : "bg-amber-100 text-amber-700"}`}>
                    {typeLabel(m.type)}
                  </span>
                </td>
                <td className={`p-2 text-right font-semibold ${m.type === "in" ? "text-emerald-700" : "text-rose-600"}`}>
                  {m.type === "in" ? "+" : "−"}{m.boxes}
                </td>
                <td className="p-2 text-slate-500">{m.note}</td>
                <td className="p-2 text-right">
                  <button className="text-xs text-slate-400 hover:text-rose-600" onClick={() => removeMovement(m.id)}>삭제</button>
                </td>
              </tr>
            ))}
            {movements.length === 0 && (
              <tr><td colSpan={5} className="p-8 text-center text-slate-400">입고 기록이 없습니다. 위에서 입고를 등록하세요.</td></tr>
            )}
          </tbody>
        </table>
      </section>

      <p className="text-xs text-slate-400">
        현 보유량 = 총 입고 − 배송 차감(배송 완료 건의 박스 합) − 택배 차감. 배송 차감은 기사 배송 완료 시 자동 반영됩니다. 타사 대행 학생의 배송·택배는 맡긴 회사 재고라 빼지 않습니다.
      </p>
    </div>
  );
}

function Stat({ title, value, tone }: { title: string; value: string; tone?: "emerald" }) {
  return (
    <div className="card p-3 text-center">
      <div className="label">{title}</div>
      <div className={`text-xl font-extrabold mt-1 ${tone === "emerald" ? "text-emerald-600" : "text-slate-700"}`}>{value}</div>
    </div>
  );
}
